import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Dialog, DialogContent, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { captureException } from '@sentry/react';
import { get } from 'lodash-es';
import { useCallback, useState } from 'react';
import { DefaultValues, FormProvider, useForm, useFormState } from 'react-hook-form';
import { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import { z } from 'zod';

import { EndContractDialog } from '~/components/contracts/EndContractDialog';
import { ContractGeneralSectionForm } from '~/components/contracts/form/ContractGeneralSectionFields';
import { ContractKpiTargetsSectionFields } from '~/components/contracts/form/ContractKpiTargetsSectionFields';
import { ContractRateSetsFields } from '~/components/contracts/form/ContractRateSetsSectionFields';
import { ContractReviewRulesFormFields } from '~/components/contracts/form/ContractReviewRulesFields';
import type { ContractFormValues } from '~/components/contracts/types';
import { ContractForm } from '~/components/contracts/types';
import DialogTitleWithCloseButton from '~/components/DialogTitleWithCloseButton';
import { createFormInvalidHandler, formatLiftAPIClientError } from '~/components/form/utils';
import {
  contractsCreate,
  contractsPartialUpdate,
} from '~/data/generated/endpoints/contracts/contracts';
import { revalidatePropertyContracts } from '~/data/hooks/useContracts';
import { getContractMutator } from '~/data/hooks/utils';
import { LiftAPIClientError } from '~/data/liftApiMutator';
import { Contract, CreateContract, UpdateContract } from '~/data/schemas/contract';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { BUTTON_SX } from '~/theme';

enum TabType {
  General = 'general',
  RateSets = 'rate-sets',
  KpiTargets = 'kpi-targets',
  ReviewRules = 'review-rules',
}

/**
 * Which section the dialog opens on, for a caller that has a reason to name one — the
 * contract page's AI Invoice Review Rules tab sends its Add rule button straight there.
 */
export type ContractFormSection = `${TabType}`;

// TabPanel component that preserves state
interface TabPanelProps {
  children: React.ReactNode;
  value: string;
  index: string;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  const isActive = value === index;

  return (
    <Box
      role="tabpanel"
      hidden={!isActive}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      sx={{
        height: '100%',
        visibility: isActive ? 'visible' : 'hidden',
        position: isActive ? 'static' : 'absolute',
        pointerEvents: isActive ? 'auto' : 'none',
        width: '100%',
      }}
    >
      {children}
    </Box>
  );
};

type EditContractProps = {
  type: 'edit';
  open: boolean;
  defaultValues: DefaultValues<ContractFormValues> | undefined;
  onClose: () => void;
  propertyId: string;
  propertyContractId: string;
  contractId: string;
  /** Section to open on. Defaults to General. */
  initialSection?: ContractFormSection;
};

type AddContractProps = {
  type: 'add';
  open: boolean;
  defaultValues: DefaultValues<ContractFormValues> | undefined;
  onClose: () => void;
  propertyId: string;
  /** Section to open on. Defaults to General. */
  initialSection?: ContractFormSection;
};

type ContractFormDialogProps = EditContractProps | AddContractProps;

type TabLabelProps = {
  label: string;
  hasErrors: boolean;
};

const TabLabel = ({ label, hasErrors }: TabLabelProps) => {
  return (
    <Typography variant="inherit" color={hasErrors ? 'error' : 'inherit'}>
      {label}
    </Typography>
  );
};

type DialogActionButtonsProps = {
  activeTab: TabType;
  onClose: () => void;
  onEndContract: () => void;
  isSaving: boolean;
};

const DialogActionButtons = ({
  activeTab,
  onClose,
  onEndContract,
  isSaving,
}: DialogActionButtonsProps) => {
  const theme = useTheme();

  const addionalButton =
    activeTab === TabType.General ? (
      <Box>
        <Button
          type="button"
          variant="outlined"
          color="error"
          onClick={onEndContract}
          sx={BUTTON_SX}
        >
          End Contract
        </Button>
      </Box>
    ) : null;

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'background.paper',
        borderTop: `1px solid ${theme.palette.divider}`,
        paddingTop: 3,
        paddingBottom: 1,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        zIndex: 5,
      }}
    >
      {addionalButton}
      <Box sx={{ display: 'flex', gap: 1, flexGrow: 1, justifyContent: 'flex-end' }}>
        <Button type="button" variant="outlined" onClick={onClose} sx={BUTTON_SX}>
          close
        </Button>
        <Button
          form="contract-form"
          loading={isSaving}
          type="submit"
          variant="contained"
          disabled={isSaving}
          sx={BUTTON_SX}
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

export const ContractFormDialog = ({
  open,
  onClose,
  propertyId,
  defaultValues,
  initialSection,
  ...props
}: ContractFormDialogProps) => {
  const theme = useTheme();
  const openOn = (initialSection as TabType | undefined) ?? TabType.General;
  const [activeTab, setActiveTab] = useState<TabType>(openOn);
  const [isEndContractDialogOpen, setIsEndContractDialogOpen] = useState(false);
  const { showSnackbar } = useLAISnackbar();

  const form = useForm<ContractFormValues, unknown, ContractForm>({
    resolver: zodResolver(ContractForm),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const { isDirty, isSubmitting, errors } = useFormState({ control: form.control });

  const handleClose = useCallback(() => {
    if (
      isDirty &&
      !window.confirm('Are you sure you want to close this contract without saving?')
    ) {
      return;
    }
    onClose();
    // Back to whichever section this dialog was asked to open on, not
    // unconditionally General — a reopen should land where the caller pointed it.
    setActiveTab(openOn);
  }, [onClose, isDirty, openOn]);

  const { trigger: updateContract } = useSWRMutation(
    // Names the mutation rather than borrowing a read key. Bound to
    // `['property-contracts', propertyId]` its automatic revalidation landed on a key
    // the contract list does not read, and its own trigger state shared a cache entry
    // with that list. What has to refresh is spelled out below instead.
    ['contract-save', propertyId],
    async (key: string[], { arg: form }: { arg: z.infer<typeof ContractForm> }) => {
      const { propertyContract, ...contract } = form;
      // The form's "None" option represents an unset service provider as '';
      // the FK on the backend only accepts a valid id or null, not ''.
      const serviceProvider = contract.serviceProvider === '' ? null : contract.serviceProvider;
      let savedContract: Contract;
      if (props.type === 'edit') {
        const contractToSave = UpdateContract.safeParse({
          ...contract,
          serviceProvider,
          propertyContracts: [propertyContract],
        });
        if (!contractToSave.success) {
          console.error('saveContract', contractToSave.error);
          throw new Error('Unable to save contract due to an internal error');
        }
        // PATCH; the hand-written UpdateContract body is tightened relative to
        // the generated PatchedContractWrite, so cast at this boundary. The
        // response is the (sparse) write serializer, but only `.id` is read
        // before the caller refetches, so cast it to Contract (AMP-504).
        const { data } = await contractsPartialUpdate(
          contractToSave.data.id,
          contractToSave.data as unknown as Parameters<typeof contractsPartialUpdate>[1],
        );
        savedContract = data as unknown as Contract;
      } else {
        const contractToSave = CreateContract.safeParse({
          ...contract,
          serviceProvider,
          propertyContracts: [propertyContract],
        });
        if (!contractToSave.success) {
          console.error('saveContract', contractToSave.error);
          throw new Error('Unable to save contract due to an internal error');
        }
        // Create returns the full Contract; the hand-written CreateContract body
        // is tightened relative to the generated ContractWrite, so cast the
        // request at this boundary (AMP-504).
        const { data } = await contractsCreate(
          contractToSave.data as unknown as Parameters<typeof contractsCreate>[0],
        );
        savedContract = data as unknown as Contract;
      }

      showSnackbar(`Contract ${props.type === 'edit' ? 'updated' : 'created'} successfully`, {
        variant: 'success',
      });

      void mutate(getContractMutator(savedContract.id));
      void revalidatePropertyContracts(propertyId);

      onClose();
    },
    {
      // Replaces the app-wide SWR onError, which would toast the raw request
      // error next to the message handleSubmit shows. A 4xx is the API
      // refusing the save, so only other errors go to Sentry.
      onError: (error) => {
        if (!(error instanceof LiftAPIClientError) || error.context.status >= 500) {
          captureException(error);
        }
      },
    },
  );

  const handleSubmit = useCallback(
    async (data: ContractForm) => {
      try {
        await updateContract(data);
      } catch (error) {
        // The dialog stays open with what the user entered, so they can fix it
        // and save again. The API's reason is shown, e.g. the 403 for a
        // contract that covers properties outside the user's accounts.
        showSnackbar(
          formatLiftAPIClientError(
            `Failed to ${props.type === 'edit' ? 'update' : 'create'} contract`,
            error,
          ),
          { variant: 'error' },
        );
      }
    },
    [updateContract, showSnackbar, props.type],
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabType) => {
    setActiveTab(newValue);
  };

  const hasRateSetsErrors = Object.keys(errors).includes('rateSets');
  const hasKpiTargetsErrors = !!get(errors, 'propertyContract.cars');
  const hasReviewRulesErrors = Object.keys(errors).includes('reviewRules');
  const hasGeneralErrors =
    Object.keys(errors).filter((key) => {
      if (key === 'rateSets') return false;
      if (key === 'propertyContract') return false;
      if (key === 'reviewRules') return false;
      return true;
    }).length > 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-modal
        keepMounted={false}
        slotProps={{
          paper: {
            sx: {
              height: '90vh',
              maxHeight: '900px',
            },
          },
        }}
      >
        <DialogTitleWithCloseButton
          onClose={handleClose}
          title={props.type === 'edit' ? 'Edit Contract Summary' : 'Create Contract'}
        />
        <DialogContent
          sx={{
            padding: theme.spacing(2),
            paddingTop: 0,
            overflow: 'auto',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ marginBottom: 3.5, borderColor: 'divider' }}
          >
            <Tab
              value={TabType.General}
              label={<TabLabel label="General" hasErrors={hasGeneralErrors} />}
            />
            <Tab
              value={TabType.RateSets}
              label={<TabLabel label="Rate Sets" hasErrors={hasRateSetsErrors} />}
            />
            <Tab
              value={TabType.KpiTargets}
              label={<TabLabel label="KPI Targets" hasErrors={hasKpiTargetsErrors} />}
            />
            <Tab
              value={TabType.ReviewRules}
              label={
                <TabLabel
                  // Matches the contract page's tab that links here.
                  label="AI Invoice Review Rules"
                  hasErrors={hasReviewRulesErrors}
                />
              }
            />
          </Tabs>

          <Box sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
            <FormProvider {...form}>
              <form
                noValidate
                id="contract-form"
                onSubmit={form.handleSubmit(
                  handleSubmit,
                  createFormInvalidHandler<ContractFormValues>({
                    formName: 'ContractFormDialog',
                    showSnackbar,
                  }),
                )}
              >
                <TabPanel value={activeTab} index={TabType.General}>
                  <ContractGeneralSectionForm />
                </TabPanel>

                <TabPanel value={activeTab} index={TabType.RateSets}>
                  <ContractRateSetsFields />
                </TabPanel>

                <TabPanel value={activeTab} index={TabType.KpiTargets}>
                  <ContractKpiTargetsSectionFields />
                </TabPanel>
                <TabPanel value={activeTab} index={TabType.ReviewRules}>
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <ContractReviewRulesFormFields />
                  </Box>
                </TabPanel>
              </form>
            </FormProvider>
          </Box>

          <DialogActionButtons
            activeTab={activeTab}
            onClose={handleClose}
            onEndContract={() => setIsEndContractDialogOpen(true)}
            isSaving={isSubmitting}
          />
        </DialogContent>
      </Dialog>

      {props.type === 'edit' && isEndContractDialogOpen ? (
        <EndContractDialog
          isOpen
          contractId={props.contractId}
          propertyId={propertyId}
          onContractEnded={() => {
            setIsEndContractDialogOpen(false);
            onClose();
            void revalidatePropertyContracts(propertyId);
          }}
          onCancel={() => setIsEndContractDialogOpen(false)}
        />
      ) : null}
    </>
  );
};
